import { useMemo,useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import MultiTable from "../../../Common/MUITable";
import {
  calculateCoveragePercent,
  filterRowsByCriteria,
} from "./reportCommon";
import {
  DEFAULT_OVERVIEW_SECONDARY_COLLECTION_FILTERS,
} from "./staticReportData";
import OverviewSecondaryCollectionPointFilter from "./OverviewSecondaryCollectionPointFilter";
import DelhiReportExport from "./DelhiReportExport";
import { SecondaryCollectionPoint } from "./ReportApi";

const getTodayDateTimeString = () => {
  const today = new Date();

  const yyyy = today.getFullYear();
  const mm = String(today.getMonth() + 1).padStart(2, "0");
  const dd = String(today.getDate()).padStart(2, "0");

  return `${yyyy}-${mm}-${dd}T00:00`;
};

const getTodayFilters = () => ({
  ...DEFAULT_OVERVIEW_SECONDARY_COLLECTION_FILTERS,
 fromDate: getTodayDateTimeString(),
  toDate: getTodayDateTimeString(),
});

const buildOverviewRow = (row) => ({
  ...row,
 id: row.collectionPointTypeId,
  // API -> UI mapping
  typeOfWaste: row.collectionPointTypeName,
  totalPoint: Number(row.total) || 0,
  cleared: Number(row.cleared) || 0,
  notCleared: Number(row.notCleared) || 0,

  // Keep existing UI column
  balance: 0,

  percentOfCleared: Number(row.percentage) || 0,
});

const buildTotalRow = (rows) => {
  const totals = rows.reduce(
    (acc, row) => ({
      totalPoint: acc.totalPoint + (Number(row.totalPoint) || 0),
      cleared: acc.cleared + (Number(row.cleared) || 0),
      notCleared: acc.notCleared + (Number(row.notCleared) || 0),
      balance: acc.balance + (Number(row.balance) || 0),
    }),
    { totalPoint: 0, cleared: 0, notCleared: 0, balance: 0 }
  );

  return {
    typeOfWaste: "Total",
    isTotalRow: true,
    totalPoint: totals.totalPoint,
    cleared: totals.cleared,
    notCleared: totals.notCleared,
    balance: totals.balance,
    percentOfCleared: calculateCoveragePercent(
      totals.totalPoint,
      totals.cleared
    ),
  };
};

const totalCellStyle = { fontWeight: 700 };

const renderTotalAwareCell = (field) => (row) =>
  row.isTotalRow ? (
    <span style={totalCellStyle}>{row[field]}</span>
  ) : (
    row[field]
  );

const exportColumns = [
  { label: "Type of waste", field: "typeOfWaste" },
  { label: "Total point", field: "totalPoint" },
  { label: "Cleared", field: "cleared" },
  { label: "Not Cleared", field: "notCleared" },
  { label: "% of cleared", field: "percentOfCleared" },
];

const OverviewSecondaryCollectionPointReport = () => {
  const navigate = useNavigate();

const [filters, setFilters] = useState(getTodayFilters());
const [appliedFilters, setAppliedFilters] = useState(getTodayFilters());

  const [reportData, setReportData] = useState([]);
const [loading, setLoading] = useState(false);

const filteredRows = useMemo(() => {
  const rows = reportData.map(buildOverviewRow);

  if (rows.length === 0) return [];

  return [...rows, buildTotalRow(rows)];
}, [reportData]);

  const handleSearch = async (values) => {
    setAppliedFilters(values);
      await fetchOverviewReport(values);
  };

const handleReset = () => {
  const todayFilters = getTodayFilters();
  setFilters(todayFilters);
  setAppliedFilters(todayFilters);
  fetchOverviewReport(todayFilters);
};

const handleTotalPointClick = (row) => {
  // Total row -> show all collection types
  if (row.isTotalRow) {
    const parentRows = filteredRows.filter(
      (item) => !item.isTotalRow
    );

    navigate(
      "/overview-of-secondary-collection-point-inner-report",
      {
        state: {
          showAllPoints: true,
          parentRows,
          appliedFilters,
        },
      }
    );

    return;
  }

  // Individual collection type -> show only this type
  navigate(
    "/overview-of-secondary-collection-point-inner-report",
    {
      state: {
        parentRow: row,
        collectionPointTypeId: row.collectionPointTypeId,
        appliedFilters,
      },
    }
  );
};

  const totalPointLinkStyle = {
    cursor: "pointer",
    color: "#003366",
    textDecoration: "underline",
    fontWeight: 600,
  };

 const tableColumns = [
  {
    label: "Type of waste",
    field: "typeOfWaste",
    width: "260px",
    render: (row) => (
      <span
        style={
          row.isTotalRow
            ? { ...totalPointLinkStyle, ...totalCellStyle }
            : totalPointLinkStyle
        }
        onClick={() => handleTotalPointClick(row)}
      >
        {row.typeOfWaste}
      </span>
    ),
  },
  {
    label: "Total point",
    field: "totalPoint",
    width: "120px",
    render: renderTotalAwareCell("totalPoint"),
  },
  {
    label: "Cleared",
    field: "cleared",
    width: "110px",
    render: renderTotalAwareCell("cleared"),
  },
  {
    label: "Not Cleared",
    field: "notCleared",
    width: "120px",
    render: renderTotalAwareCell("notCleared"),
  },
  // {
  //   label: "Balance",
  //   field: "balance",
  //   width: "110px",
  //   render: renderTotalAwareCell("balance"),
  // },
  {
    label: "% of cleared",
    field: "percentOfCleared",
    width: "130px",
    render: renderTotalAwareCell("percentOfCleared"),
  },
];

useEffect(() => {
  fetchOverviewReport(getTodayFilters());
}, []);

const fetchOverviewReport = async (filterValues) => {
  try {
    setLoading(true);

const response = await SecondaryCollectionPoint({
  fromDate: filterValues.fromDate,
  toDate: filterValues.toDate,
});

    console.log("API RESPONSE:", response.data);

    if (Array.isArray(response.data)) {
      setReportData(response.data);
    } else {
      setReportData([]);
    }
  } catch (error) {
    console.error(
      "Error fetching secondary collection overview:",
      error
    );

    setReportData([]);
  } finally {
    setLoading(false);
  }
};

  return (
    <div className="content text-start">
      <div className="page-content">
        <div className="d-flex flex-column flex-md-row justify-content-between align-items-start gap-3 mt-3 mb-3">
          <div className="me-md-3">
            <h5 className="mb-1 fw-semibold">
              Overview of Secondary Collection Point
            </h5>
            <p className="text-muted mb-0">
              View overview of secondary collection point report.
            </p>
          </div>
          <DelhiReportExport
            reportData={filteredRows}
            reportTitle="Overview of Secondary Collection Point"
            columns={exportColumns}
            fileName="OverviewSecondaryCollectionPointReport"
          />
        </div>

        <div className="card shadow mb-3">
          <div className="card-body">
            <OverviewSecondaryCollectionPointFilter
              initialValues={filters}
              onSearch={(values) => {
                setFilters(values);
                handleSearch(values);
              }}
              onReset={handleReset}
            />
          </div>
        </div>

        <div className="card shadow">
          <div className="card-body">
            <div className="top-header text-center">
              <span>Overview of Secondary Collection Point</span>
            </div>
            <div className="table-responsive">
              <MultiTable
              loading={loading}
                needSRNumber
                columns={tableColumns}
                rows={filteredRows}
                tableHeight={500}
                hasTotalRow
                rowsPerPageOptions={[10, 25, 50, 100]}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OverviewSecondaryCollectionPointReport;
